import React, { useState, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  X,
  Landmark,
  ShieldCheck,
  Edit3,
  Plus,
  AlertCircle,
} from "lucide-react";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { API_ENDPOINTS } from "../lib/config";
import { fetchWithAuth } from "../lib/authService";

export default function BankAccountModal({ isOpen, onClose }) {
  const [loading, setLoading] = useState(false);
  const [beneficiary, setBeneficiary] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [apiError, setApiError] = useState(null);

  // Form state
  const [accountNumber, setAccountNumber] = useState("");
  const [confirmAccount, setConfirmAccount] = useState("");
  const [ifsc, setIfsc] = useState("");
  const [name, setName] = useState("");
  const [errors, setErrors] = useState({});

  const fetchBeneficiary = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetchWithAuth(API_ENDPOINTS.BENEFICIARY, { method: "GET" });
      if (res.ok) {
        const data = await res.json();
        if (data && (data.bankAccount || data.accountNumber)) {
          setBeneficiary({
            bankAccount: data.bankAccount || data.accountNumber,
            ifsc: data.ifsc,
            name: data.name || data.beneficiaryName,
            status: data.status,
          });
          setIsEditing(false);
        } else {
          setBeneficiary(null);
        }
      } else {
        setBeneficiary(null);
      }
    } catch (err) {
      console.error("Error fetching beneficiary:", err);
      setBeneficiary(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isOpen) {
      fetchBeneficiary();
    } else {
      setIsEditing(false);
      setErrors({});
      setApiError(null);
    }
  }, [isOpen, fetchBeneficiary]);

  const handleOpenEdit = () => {
    if (beneficiary) {
      setAccountNumber(beneficiary.bankAccount || "");
      setConfirmAccount(beneficiary.bankAccount || "");
      setIfsc(beneficiary.ifsc || "");
      setName(beneficiary.name || "");
    } else {
      setAccountNumber("");
      setConfirmAccount("");
      setIfsc("");
      setName("");
    }
    setErrors({});
    setApiError(null);
    setIsEditing(true);
  };

  const validate = () => {
    setApiError(null);
    const errs = {};
    const acc = accountNumber.trim();
    const confAcc = confirmAccount.trim();
    const ifscCode = ifsc.trim().toUpperCase();
    const holderName = name.trim();

    if (!acc) {
      errs.accountNumber = "Account number is required";
    } else if (acc.length < 6 || acc.length > 20) {
      errs.accountNumber = "Enter a valid account number (6-20 digits)";
    }

    if (!confAcc) {
      errs.confirmAccount = "Please confirm your account number";
    } else if (acc !== confAcc) {
      errs.confirmAccount = "Account numbers do not match";
    }

    if (!ifscCode) {
      errs.ifsc = "IFSC code is required";
    } else if (!/^[A-Z]{4}0[A-Z0-9]{6}$/.test(ifscCode)) {
      errs.ifsc = "Invalid IFSC format (e.g. HDFC0001234)";
    }

    if (!holderName) {
      errs.name = "Account holder name is required";
    } else if (holderName.length < 2) {
      errs.name = "Enter a valid name";
    }

    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    setSaving(true);
    setApiError(null);
    try {
      const payload = {
        type: "partner",
        bankAccount: accountNumber.trim(),
        ifsc: ifsc.trim().toUpperCase(),
        name: name.trim(),
      };

      const res = await fetchWithAuth(API_ENDPOINTS.ADD_BENEFICIARY, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        let errMsg = "Failed to save bank details";
        if (typeof errData.message === "string") {
          errMsg = errData.message;
        } else if (Array.isArray(errData.message) && errData.message.length > 0) {
          errMsg = errData.message.join(", ");
        } else if (typeof errData.error === "string") {
          errMsg = errData.error;
        }
        throw new Error(errMsg);
      }

      toast.success("Bank details saved successfully!");
      await fetchBeneficiary();
      setIsEditing(false);
      setApiError(null);
    } catch (err) {
      console.error("Save bank details error:", err);
      const errMsg =
        err.message || "Error saving bank details. Please check information.";
      setApiError(errMsg);
      toast.error(errMsg);
    } finally {
      setSaving(false);
    }
  };

  const maskAccount = (acc) => {
    if (!acc) return "••••••••";
    if (acc.length > 4) {
      return "••••••" + acc.slice(-4);
    }
    return acc;
  };

  if (!isOpen || typeof document === "undefined") return null;

  return createPortal(
    <AnimatePresence>
      <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          className="relative w-full max-w-lg overflow-hidden rounded-2xl bg-white shadow-2xl border border-purple-100"
        >
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-100 bg-gradient-to-r from-purple-50 via-white to-purple-50">
            <div className="flex items-center gap-3">
              <div className="p-2.5 rounded-xl bg-[#72288D]/10 text-[#72288D]">
                <Landmark className="w-5 h-5" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-900">
                  {isEditing ? "Bank Account Details" : "Payout Bank Account"}
                </h3>
                <p className="text-xs text-gray-500">
                  {isEditing
                    ? "Fill in your bank account details for instant payouts"
                    : "Manage your beneficiary details for event & tip earnings"}
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              disabled={saving}
              className="p-1.5 rounded-lg text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Body */}
          <div className="p-6">
            {loading ? (
              <div className="flex flex-col items-center justify-center py-12 space-y-3">
                <div className="w-8 h-8 border-4 border-[#72288D] border-t-transparent rounded-full animate-spin" />
                <p className="text-sm text-gray-500 font-medium">Checking bank account...</p>
              </div>
            ) : isEditing ? (
              /* Add / Edit Form */
              <form onSubmit={handleSave} className="space-y-4">
                <div>
                  <label className="block text-xs font-semibold uppercase tracking-wider text-gray-700 mb-1">
                    Account Holder Name
                  </label>
                  <input
                    type="text"
                    value={name}
                    onChange={(e) => {
                      setName(e.target.value);
                      if (errors.name) setErrors((p) => ({ ...p, name: null }));
                      if (apiError) setApiError(null);
                    }}
                    placeholder="Enter full name as per bank records"
                    className={`w-full px-4 py-2.5 rounded-xl border text-sm focus:outline-none transition-colors ${
                      errors.name
                        ? "border-red-400 bg-red-50/30 focus:border-red-500"
                        : "border-gray-200 focus:border-[#72288D]"
                    }`}
                  />
                  {errors.name && (
                    <p className="text-xs text-red-500 mt-1 flex items-center gap-1">
                      <AlertCircle className="w-3.5 h-3.5" /> {errors.name}
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-xs font-semibold uppercase tracking-wider text-gray-700 mb-1">
                    Bank Account Number
                  </label>
                  <input
                    type="text"
                    value={accountNumber}
                    onChange={(e) => {
                      setAccountNumber(e.target.value);
                      if (errors.accountNumber)
                        setErrors((p) => ({ ...p, accountNumber: null }));
                      if (apiError) setApiError(null);
                    }}
                    placeholder="Enter your bank account number"
                    className={`w-full px-4 py-2.5 rounded-xl border text-sm focus:outline-none transition-colors ${
                      errors.accountNumber
                        ? "border-red-400 bg-red-50/30 focus:border-red-500"
                        : "border-gray-200 focus:border-[#72288D]"
                    }`}
                  />
                  {errors.accountNumber && (
                    <p className="text-xs text-red-500 mt-1 flex items-center gap-1">
                      <AlertCircle className="w-3.5 h-3.5" /> {errors.accountNumber}
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-xs font-semibold uppercase tracking-wider text-gray-700 mb-1">
                    Confirm Account Number
                  </label>
                  <input
                    type="password"
                    value={confirmAccount}
                    onChange={(e) => {
                      setConfirmAccount(e.target.value);
                      if (errors.confirmAccount)
                        setErrors((p) => ({ ...p, confirmAccount: null }));
                      if (apiError) setApiError(null);
                    }}
                    placeholder="Re-enter bank account number"
                    className={`w-full px-4 py-2.5 rounded-xl border text-sm focus:outline-none transition-colors ${
                      errors.confirmAccount
                        ? "border-red-400 bg-red-50/30 focus:border-red-500"
                        : "border-gray-200 focus:border-[#72288D]"
                    }`}
                  />
                  {errors.confirmAccount && (
                    <p className="text-xs text-red-500 mt-1 flex items-center gap-1">
                      <AlertCircle className="w-3.5 h-3.5" /> {errors.confirmAccount}
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-xs font-semibold uppercase tracking-wider text-gray-700 mb-1">
                    IFSC Code
                  </label>
                  <input
                    type="text"
                    value={ifsc}
                    onChange={(e) => {
                      setIfsc(e.target.value.toUpperCase());
                      if (errors.ifsc) setErrors((p) => ({ ...p, ifsc: null }));
                      if (apiError) setApiError(null);
                    }}
                    placeholder="e.g. HDFC0001234"
                    maxLength={11}
                    className={`w-full px-4 py-2.5 rounded-xl border text-sm uppercase focus:outline-none transition-colors font-mono ${
                      errors.ifsc
                        ? "border-red-400 bg-red-50/30 focus:border-red-500"
                        : "border-gray-200 focus:border-[#72288D]"
                    }`}
                  />
                  {errors.ifsc && (
                    <p className="text-xs text-red-500 mt-1 flex items-center gap-1">
                      <AlertCircle className="w-3.5 h-3.5" /> {errors.ifsc}
                    </p>
                  )}
                </div>

                {apiError && (
                  <div className="flex items-start gap-2.5 p-3 rounded-xl bg-red-50 border border-red-200 text-red-700 text-xs">
                    <AlertCircle className="w-4 h-4 text-red-500 shrink-0 mt-0.5" />
                    <div className="flex-1 font-medium leading-relaxed">
                      {apiError}
                    </div>
                  </div>
                )}

                <div className="flex items-center justify-end gap-3 pt-4 border-t border-gray-100">
                  {beneficiary && (
                    <button
                      type="button"
                      onClick={() => {
                        setIsEditing(false);
                        setApiError(null);
                      }}
                      disabled={saving}
                      className="px-4 py-2.5 rounded-xl text-sm font-medium text-gray-600 hover:bg-gray-100 transition-colors"
                    >
                      Cancel
                    </button>
                  )}
                  <button
                    type="submit"
                    disabled={saving}
                    className="px-6 py-2.5 rounded-xl text-sm font-medium text-white bg-[#72288D] hover:bg-[#72288D]/90 shadow-md shadow-[#72288D]/20 transition-all flex items-center gap-2"
                  >
                    {saving ? (
                      <>
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        <span>Saving...</span>
                      </>
                    ) : (
                      <span>Save Bank Account</span>
                    )}
                  </button>
                </div>
              </form>
            ) : beneficiary ? (
              /* View Verified Beneficiary Card */
              <div className="space-y-6">
                <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-[#2D0A4A] to-[#6720A3] p-6 text-white shadow-xl">
                  {/* Background decoration */}
                  <div className="absolute top-0 right-0 -mr-8 -mt-8 w-32 h-32 bg-white/10 rounded-full blur-xl pointer-events-none" />

                  <div className="flex items-center justify-between mb-4">
                    <span className="text-xs font-semibold tracking-wider uppercase text-purple-200">
                      Primary Beneficiary
                    </span>
                    <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-emerald-500/20 text-emerald-300 text-xs font-medium border border-emerald-400/30">
                      <ShieldCheck className="w-3.5 h-3.5" />
                      <span>Account Verified</span>
                    </div>
                  </div>

                  <div className="space-y-3">
                    <div>
                      <div className="text-xs text-purple-200">Account Number</div>
                      <div className="text-2xl font-mono font-bold tracking-wider mt-0.5">
                        {maskAccount(beneficiary.bankAccount)}
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4 pt-2 border-t border-white/10">
                      <div>
                        <div className="text-[11px] text-purple-200">Account Holder</div>
                        <div className="text-sm font-semibold truncate mt-0.5">
                          {beneficiary.name || "—"}
                        </div>
                      </div>
                      <div>
                        <div className="text-[11px] text-purple-200">IFSC Code</div>
                        <div className="text-sm font-mono font-semibold mt-0.5">
                          {beneficiary.ifsc || "—"}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="flex items-center justify-between pt-2">
                  <span className="text-xs text-gray-500">
                    Payouts are routed directly to this verified account.
                  </span>
                  <button
                    type="button"
                    onClick={handleOpenEdit}
                    className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold text-[#72288D] bg-purple-50 hover:bg-purple-100 transition-colors"
                  >
                    <Edit3 className="w-4 h-4" />
                    <span>Change Details</span>
                  </button>
                </div>
              </div>
            ) : (
              /* Empty State */
              <div className="flex flex-col items-center justify-center py-10 text-center space-y-4">
                <div className="p-4 rounded-full bg-purple-50 text-[#72288D]">
                  <Landmark className="w-10 h-10 stroke-1" />
                </div>
                <div>
                  <h4 className="text-base font-bold text-gray-900">
                    No Bank Account Added
                  </h4>
                  <p className="text-xs text-gray-500 max-w-xs mt-1">
                    Add your bank account to receive automated weekly earnings and gift tips directly into your account.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={handleOpenEdit}
                  className="mt-2 flex items-center gap-2 px-6 py-2.5 rounded-xl text-sm font-medium text-white bg-[#72288D] hover:bg-[#72288D]/90 shadow-md shadow-[#72288D]/20 transition-all"
                >
                  <Plus className="w-4 h-4" />
                  <span>Add Bank Account</span>
                </button>
              </div>
            )}
          </div>
        </motion.div>
      </div>
    </AnimatePresence>,
    document.body
  );
}
